import { getProductInfo } from './productHelpers.js';
import { sortModifierGroups, sortModifierOptions, getExtraQuantity } from './modifierHelpers.js';

// Sentinel a condition/reward's product_ids can contain instead of (or
// alongside) explicit ids, meaning "any product" — lets sok-Admin author a
// storewide promotion (e.g. "30% off the whole menu") without hand-picking
// every product, and without missing products added later. An
// empty/missing product_ids array still means "matches nothing" — that
// stays the safe default when a condition was left unset by mistake.
const ALL_PRODUCTS_ID = '__all__';

const matchesProductIds = (productIds, productId) => {
  if (!productIds || productIds.length === 0) return false;
  return productIds.includes(ALL_PRODUCTS_ID) || productIds.includes(productId);
};

const safeToDate = (val) => {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val.seconds !== undefined) return new Date(val.seconds * 1000);
  return new Date(val);
};

const isPromotionValidByDate = (promotion) => {
  const now = new Date();

  if (promotion.constraints) {
    if (promotion.constraints.valid_from) {
      if (now < safeToDate(promotion.constraints.valid_from)) return false;
    }
    if (promotion.constraints.valid_to) {
      if (now > safeToDate(promotion.constraints.valid_to)) return false;
    }
    if (promotion.constraints.valid_time_from && promotion.constraints.valid_time_to) {
      const from = safeToDate(promotion.constraints.valid_time_from);
      const to = safeToDate(promotion.constraints.valid_time_to);
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const fromMinutes = from.getHours() * 60 + from.getMinutes();
      const toMinutes = to.getHours() * 60 + to.getMinutes();

      if (fromMinutes <= toMinutes) {
        if (currentMinutes < fromMinutes || currentMinutes > toMinutes) return false;
      } else {
        if (currentMinutes < fromMinutes && currentMinutes > toMinutes) return false;
      }
    }
  }
  return true;
};

/**
 * Gates a promotion on the shopper's loyalty identification for this cart session.
 * `loyaltyContext.identifiedPrograms` is keyed by program_id and holds the
 * identifyLoyaltyProgram response (`{ member_id, program_id, membership }`);
 * `loyaltyContext.programsById` holds the loyalty_programs config (needed for
 * MIN_TIER, since tier order lives on the program, not on the membership).
 */
const isPromotionValidByLoyalty = (promotion, loyaltyContext) => {
  const requirement = promotion.constraints?.loyalty_requirement;
  if (!requirement) return true;

  const { identifiedPrograms = {}, programsById = {} } = loyaltyContext || {};
  const membership = identifiedPrograms[requirement.program_id]?.membership;
  if (!membership) return false;

  if (!meetsLoyaltyRequirement(requirement, membership, programsById)) return false;

  // Being entitled to a promotion is not the same as having any left: a per-customer cap
  // is spent across sales, so a member who used it up stops seeing it altogether.
  return remainingUnitsForCustomer(promotion, loyaltyContext) > 0;
};

const meetsLoyaltyRequirement = (requirement, membership, programsById) => {
  switch (requirement.type) {
    case 'AUTHENTICATED':
      return membership.status === 'authenticated';
    case 'MIN_STAMPS':
      return (membership.stamps?.[requirement.category_id] || 0) >= requirement.min_count;
    case 'MIN_TIER': {
      const tiers = programsById[requirement.program_id]?.tiers || [];
      const memberOrder = tiers.find(t => t.id === membership.tier_id)?.order;
      const minOrder = tiers.find(t => t.id === requirement.min_tier_id)?.order;
      return memberOrder !== undefined && minOrder !== undefined && memberOrder >= minOrder;
    }
    default:
      return false;
  }
};

/**
 * How many units of a promotion this shopper may still get, given what they already
 * used. `promotions_usage` comes from identifyLoyaltyProgram, which sums it from the
 * member's recorded purchases.
 *
 * With nobody identified there is no history to subtract and the cap falls back to
 * applying per cart, which is how it has always behaved for an anonymous shopper.
 */
const remainingUnitsForCustomer = (promotion, loyaltyContext) => {
  const limit = promotion.constraints?.max_units_per_customer;
  if (!limit) return Infinity;

  const programId = promotion.constraints?.loyalty_requirement?.program_id;
  const membership = programId
    ? loyaltyContext?.identifiedPrograms?.[programId]?.membership
    : null;
  const used = membership?.promotions_usage?.[promotion.id]?.units || 0;

  return Math.max(0, limit - used);
};

/**
 * Selects the applicable rule from a promotion's rules array based on the
 * current store and zone context. Rules are sorted by sort_order and the
 * first matching one wins. If the winning rule has no conditions, they are
 * inherited from the default rule.
 */
const selectRule = (promo, filteredZones, storeId) => {
  const rules = promo.rules;
  if (!rules) return null;
  if (!Array.isArray(rules)) return rules; // backward compat

  const filteredZoneIds = (filteredZones || []).map(z => z.id);
  const currentStoreId = storeId ? String(storeId) : null;

  const validRules = rules.filter(rule => {
    const zoneIds = rule.zone_ids || [];
    const ruleStoreIds = rule.store_ids || [];
    const zoneMatch = zoneIds.length === 0 || zoneIds.some(zid => filteredZoneIds.includes(zid));
    const storeMatch = ruleStoreIds.length === 0 || (currentStoreId && ruleStoreIds.includes(currentStoreId));
    return zoneMatch && storeMatch;
  });

  if (validRules.length === 0) return null;

  validRules.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const selected = validRules[0];

  // Non-default rules often have empty conditions — inherit from the default rule
  if (!selected.conditions?.length) {
    const defaultRule = rules.find(r => r.is_default);
    if (defaultRule?.conditions?.length) {
      return { ...selected, conditions: defaultRule.conditions };
    }
  }

  return selected;
};

/**
 * Applies promotions to the given cart items.
 * Returns { items, totalPromoDiscount }
 */
export function applyPromotions(cartItems, promotions, products, modifiers, filteredStoreIds, filteredZones, storeId, loyaltyContext) {
  if (!cartItems || !cartItems.length || !promotions || !promotions.length) {
    return { items: cartItems, totalPromoDiscount: 0 };
  }

  // 1. Flatten cart items into individual units
  let flattenedItems = [];
  cartItems.forEach((cartItem) => {
    const product = products.find(p => p.id === cartItem.productId);
    if (!product) return;

    const productModifiers = modifiers ? modifiers.filter(m => (product.modifiers || []).includes(m.id)) : [];
    const { commonPrice } = getProductInfo(product, filteredStoreIds);

    let extraSubtotal = 0;
    // Mismo orden que ProductDetailModal para que el `groupIndex` de las llaves coincida
    sortModifierGroups(productModifiers).forEach((extraGroup, groupIndex) => {
      sortModifierOptions(extraGroup.options).forEach(option => {
        const quantity = getExtraQuantity(cartItem.selectedExtras, extraGroup, option, groupIndex);
        if (quantity > 0) extraSubtotal += ((option.price || 0) * quantity);
      });
    });

    const unitPrice = commonPrice + extraSubtotal;

    for (let i = 0; i < cartItem.quantity; i++) {
      flattenedItems.push({
        refHash: `${cartItem.id}-${i}`,
        cartItemId: cartItem.id,
        productId: cartItem.productId,
        unitPrice,
        priceRemaining: unitPrice,
        isUsed: false,
        usedByPromoIds: [],
        promotions: []
      });
    }
  });

  // 2. Filter valid promotions by date and loyalty requirement
  const validPromotions = promotions.filter(
    (p) => p && p.is_active !== false && isPromotionValidByDate(p) && isPromotionValidByLoyalty(p, loyaltyContext)
  );

  // 3. Sort by type priority
  validPromotions.sort((a, b) => {
    const rank = (p) => {
      if (p.type === 'XXY_CHEAPEST_FREE') return 1;
      if (p.type === 'BUYQTY_GET_ITEM_FREE') return 2;
      return 3;
    };
    return rank(a) - rank(b);
  });

  // Helper: get eligible flattened items for a set of productIds
  const getApplicableItems = (productIds, promoId, stackable) => {
    return flattenedItems.filter(item => {
      const isProductMatch = matchesProductIds(productIds, item.productId);
      const isNotUsed = !item.isUsed || stackable;
      const isNotUsedByPromo = !item.usedByPromoIds.includes(promoId);
      const isPriceRemaining = item.priceRemaining > 0;
      return isProductMatch && isNotUsed && isNotUsedByPromo && isPriceRemaining;
    });
  };

  const markUsed = (items, promoId, stackable) => {
    items.forEach(item => {
      item.usedByPromoIds.push(promoId);
      if (!stackable) item.isUsed = true;
    });
  };

  // 4. Process each promotion
  validPromotions.forEach(promo => {
    const selectedRule = selectRule(promo, filteredZones, storeId);
    if (!selectedRule) return;

    const conditions = selectedRule.conditions || [];
    const rewards = selectedRule.rewards || [];
    const condition = conditions[0] || {};
    const reward = rewards[0] || {};

    const maxUses = promo.constraints?.max_uses_per_order ?? Infinity;
    // Not the raw cap but what is left of it: an identified member who already used part
    // of their allowance only gets the remainder in this cart.
    const maxUnits = remainingUnitsForCustomer(promo, loyaltyContext);
    const stackable = promo.constraints?.stackable ?? false;

    if (promo.type === 'XXY_CHEAPEST_FREE') {
      const conditionProductIds = condition.product_ids || [];
      const minQty = condition.min_qty || 2;
      const repeatEvery = condition.repeat_every || minQty;
      // The price the cheapest unit ends at, not the discount it gets — same meaning as
      // in COMBO_FIXED_PRICE, and what sok-Admin's "Precio final de la más barata" field
      // and the extractor (fixed_price_total / quantity) both write. Absent means free.
      const fixedPrice = reward.fixed_price ?? 0;

      const applicableItems = getApplicableItems(conditionProductIds, promo.id, stackable);
      if (applicableItems.length < minQty) return;

      // Sort DESC: most expensive first, every repeat_every-th item gets discounted
      applicableItems.sort((a, b) => b.unitPrice - a.unitPrice);

      // Each pass frees exactly one unit, so numApplied is also the unit count and can be
      // checked against the customer's remaining allowance directly.
      let numApplied = 0;
      for (let i = repeatEvery - 1; i < applicableItems.length && numApplied < maxUses && numApplied < maxUnits; i += repeatEvery) {
        const item = applicableItems[i];
        const discountVal = Math.max(0, item.priceRemaining - fixedPrice);
        if (discountVal > 0) {
          item.priceRemaining -= discountVal;
          item.promotions.push({ id: promo.id, name: promo.name, discount: discountVal, useIndex: numApplied });
        }
        numApplied++;
      }

      markUsed(applicableItems, promo.id, stackable);

    } else if (promo.type === 'PERCENT_SIMPLE' || promo.type === 'TIERED_QTY_PERCENT') {
      const discountPercent = reward.discount_value || 0;
      const rewardProductIds = reward.product_ids || [];

      let usesCount = 0;
      let unitsDiscountedCount = 0;
      let loopSafety = 0;

      while (usesCount < maxUses && unitsDiscountedCount < maxUnits) {
        if (loopSafety++ > 1000) {
          console.error("INFINITE LOOP DETECTED IN PERCENT_SIMPLE!", { promoId: promo.id, usesCount, maxUses, unitsDiscountedCount, maxUnits });
          break;
        }
        let bundleItems = [];
        let allConditionsMet = true;

        for (const cond of conditions) {
          const requiredQty = cond.min_qty || 1;
          const condProductIds = cond.product_ids || [];
          const available = getApplicableItems(condProductIds, promo.id, stackable)
            .filter(it => !bundleItems.some(b => b.refHash === it.refHash));

          if (available.length >= requiredQty) {
            bundleItems.push(...available.slice(0, requiredQty));
          } else {
            allConditionsMet = false;
            break;
          }
        }

        if (!allConditionsMet || bundleItems.length === 0) break;

        for (const item of bundleItems) {
          item.usedByPromoIds.push(promo.id);
          if (rewardProductIds.length === 0 || rewardProductIds.includes(ALL_PRODUCTS_ID) || rewardProductIds.includes(item.productId)) {
            if (unitsDiscountedCount >= maxUnits) break;
            const discountVal = item.priceRemaining * (discountPercent / 100);
            item.priceRemaining -= discountVal;
            item.promotions.push({ id: promo.id, name: promo.name, discount: discountVal, useIndex: usesCount });
            unitsDiscountedCount++;
          }
          if (!stackable) item.isUsed = true;
        }
        usesCount++;
      }

    } else if (promo.type === 'COMBO_FIXED_PRICE') {
      let usesCount = 0;

      while (usesCount < maxUses) {
        let bundleItems = [];
        let allConditionsMet = true;

        for (const cond of conditions) {
          const requiredQty = cond.min_qty || 1;
          const condProductIds = cond.product_ids || [];
          const available = getApplicableItems(condProductIds, promo.id, stackable)
            .filter(it => !bundleItems.some(b => b.refHash === it.refHash));

          if (available.length >= requiredQty) {
            bundleItems.push(...available.slice(0, requiredQty));
          } else {
            allConditionsMet = false;
            break;
          }
        }

        if (!allConditionsMet) break;

        let anyDiscountApplied = false;
        rewards.forEach(rewardItem => {
          const rewardProductIds = rewardItem.product_ids || [];
          const fixedPrice = rewardItem.fixed_price;
          if (fixedPrice === undefined) return;

          let candidates = bundleItems.filter(it => matchesProductIds(rewardProductIds, it.productId));
          if (rewardItem.apply_strategy === 'CHEAPEST_UNIT') {
            candidates.sort((a, b) => a.priceRemaining - b.priceRemaining);
            candidates = candidates.slice(0, 1);
          }

          candidates.forEach(item => {
            if (item.priceRemaining > fixedPrice) {
              const discountVal = item.priceRemaining - fixedPrice;
              item.priceRemaining -= discountVal;
              item.promotions.push({ id: promo.id, name: promo.name, discount: discountVal, useIndex: usesCount });
              anyDiscountApplied = true;
            }
          });
        });

        markUsed(bundleItems, promo.id, stackable);

        if (!anyDiscountApplied) break;
        usesCount++;
      }

    } else if (promo.type === 'BUYQTY_GET_ITEM_FREE') {
      let usesCount = 0;

      while (usesCount < maxUses) {
        // Check all buy conditions are met
        let buyItems = [];
        let allConditionsMet = true;

        for (const cond of conditions) {
          const requiredQty = cond.min_qty || 1;
          const condProductIds = cond.product_ids || [];
          const available = getApplicableItems(condProductIds, promo.id, stackable)
            .filter(it => !buyItems.some(b => b.refHash === it.refHash));

          if (available.length >= requiredQty) {
            buyItems.push(...available.slice(0, requiredQty));
          } else {
            allConditionsMet = false;
            break;
          }
        }

        if (!allConditionsMet) break;

        // Make the first available item from each reward's product_ids free
        let anyFreeApplied = false;
        rewards.forEach(rewardItem => {
          const rewardProductIds = rewardItem.product_ids || [];
          const freeItem = getApplicableItems(rewardProductIds, promo.id, stackable)
            .find(it => !buyItems.some(b => b.refHash === it.refHash));

          if (freeItem && freeItem.priceRemaining > 0) {
            const discountVal = freeItem.priceRemaining;
            freeItem.priceRemaining = 0;
            freeItem.promotions.push({ id: promo.id, name: promo.name, discount: discountVal, useIndex: usesCount });
            freeItem.usedByPromoIds.push(promo.id);
            if (!stackable) freeItem.isUsed = true;
            anyFreeApplied = true;
          }
        });

        markUsed(buyItems, promo.id, stackable);

        if (!anyFreeApplied) break;
        usesCount++;
      }
    }
  });

  // 5. Reconstruct cart items with aggregated promotions
  const enrichedCartItems = JSON.parse(JSON.stringify(cartItems));
  let totalPromoDiscount = 0;

  enrichedCartItems.forEach(cartItem => {
    cartItem.promoDiscount = 0;
    cartItem.appliedPromotions = [];

    const itemUnits = flattenedItems.filter(f => f.cartItemId === cartItem.id);

    // Each discount carries the index of the rule application that produced it, so the
    // count of distinct indexes is how many times the promotion fired, while the number
    // of discounts is how many units it touched. A 2x1 over two units fires once.
    const activationsByPromoId = new Map();

    itemUnits.forEach(unit => {
      unit.promotions.forEach(promo => {
        cartItem.promoDiscount += promo.discount;
        totalPromoDiscount += promo.discount;

        if (!activationsByPromoId.has(promo.id)) {
          activationsByPromoId.set(promo.id, new Set());
        }
        activationsByPromoId.get(promo.id).add(promo.useIndex);

        const existing = cartItem.appliedPromotions.find(p => p.id === promo.id);
        if (existing) {
          existing.discount += promo.discount;
          existing.units += 1;
        } else {
          cartItem.appliedPromotions.push({
            id: promo.id,
            name: promo.name,
            discount: promo.discount,
            units: 1,
            quantity: 0
          });
        }
      });
    });

    cartItem.appliedPromotions.forEach(applied => {
      applied.quantity = activationsByPromoId.get(applied.id).size;
    });
  });

  return { items: enrichedCartItems, totalPromoDiscount };
}
